import React from 'react';
import { Box, Typography, Button } from '@mui/material';

/**
 * EmptyState Component
 * Displays a placeholder when there is no content to show
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Main message
 * @param {string} props.description - Additional description
 * @param {React.ReactNode} props.icon - Icon to display
 * @param {string} props.actionText - Text for the action button
 * @param {Function} props.onAction - Function to call when action button is clicked
 * @param {Object} props.sx - Additional styles
 * @returns {React.ReactElement} Empty state component
 */
const EmptyState = ({
  title = 'No items found',
  description = 'There are no items to display at this time.',
  icon,
  actionText,
  onAction,
  sx = {},
}) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      py={6}
      px={3}
      sx={{
        borderRadius: 2,
        backgroundColor: 'white',
        height: '100%',
        minHeight: 400,
        width: '100%',
        className: 'empty-state',
        ...sx
      }}
    >
      {/* Icon */}
      {icon && (
        <Box sx={{ mb: 2, color: 'text.secondary' }}>
          {icon}
        </Box>
      )}
      
      {/* Title */}
      <Typography variant="h6" component="h3" gutterBottom>
        {title}
      </Typography>
      
      {/* Description */}
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, mb: 3 }}>
        {description}
      </Typography>
      
      {/* Action Button */}
      {actionText && onAction && (
        <Button
          variant="contained"
          color="primary"
          onClick={onAction}
        >
          {actionText}
        </Button>
      )}
    </Box>
  );
};

export default EmptyState;